import React from 'react';
import { Card } from '../ui/card';
import { Plane, Package, ArrowRight, CheckCircle, Shield, User, Store } from 'lucide-react';

export const HowItWorks = () => {
  const steps = [
    { num: '01', title: 'Реєстрація', desc: 'Створіть акаунт на сайті за 1 хвилину та отримайте персональний код клієнта для складів у Китаї та США.', icon: User, glow: '#3b82f6', bg: 'from-blue-500 via-blue-600 to-indigo-700' },
    { num: '02', title: 'Знайдіть товар', desc: 'Оберіть товар на 1688, Taobao, Alibaba, Amazon або іншому маркетплейсі. Або скористайтесь нашою послугою викупу.', icon: Store, glow: '#f97316', bg: 'from-orange-400 via-orange-500 to-red-500' },
    { num: '03', title: 'Вкажіть наш склад', desc: 'При оформленні замовлення вкажіть адресу нашого складу та ваш код клієнта. Ми приймемо вашу посилку.', icon: Package, glow: '#a855f7', bg: 'from-purple-500 via-purple-600 to-fuchsia-600' },
    { num: '04', title: 'Консолідація', desc: 'На складі ми перевіримо якість, сфотографуємо товар та об\'єднаємо кілька замовлень в одну посилку для економії.', icon: Shield, glow: '#22c55e', bg: 'from-emerald-400 via-green-500 to-teal-600' },
    { num: '05', title: 'Доставка', desc: 'Обирайте авіа (7-14 днів) або морську (30-45 днів) доставку. Відстежуйте посилку в особистому кабінеті.', icon: Plane, glow: '#0ea5e9', bg: 'from-cyan-400 via-sky-500 to-blue-600' },
    { num: '06', title: 'Отримання', desc: 'Посилка приїжджає в Україну — ми відправляємо Новою Поштою або кур\'єром до дверей. Готово!', icon: CheckCircle, glow: '#10b981', bg: 'from-green-400 via-emerald-500 to-teal-600' },
  ];
  return (
    <section id="how-it-works" className="py-20 md:py-28 bg-[#060a13] relative overflow-hidden">
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-[0.07]" style={{backgroundImage: 'linear-gradient(rgba(59,130,246,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.5) 1px, transparent 1px)', backgroundSize: '60px 60px'}} />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[150px]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Як це працює<span className="text-blue-400">?</span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Замовити доставку з Китаю та США — просто. Ось 6 кроків від покупки до отримання.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={i}
              className="group relative"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              {/* Card */}
              <div className="relative bg-[#111827]/80 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 h-full transition-all duration-500 hover:border-transparent hover:shadow-[0_0_40px_-12px_var(--glow)] overflow-hidden"
                style={{ '--glow': s.glow }}>

                {/* Glow line on top */}
                <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r ${s.bg} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                {/* Animated corner sparks */}
                <div className="absolute top-2 right-2 w-1 h-1 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping" style={{ backgroundColor: s.glow }} />
                <div className="absolute bottom-2 left-2 w-1 h-1 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping" style={{ backgroundColor: s.glow, animationDelay: '0.5s' }} />

                {/* Icon */}
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${s.bg} flex items-center justify-center mb-6 shadow-lg transition-all duration-500 group-hover:scale-110 group-hover:shadow-[0_0_30px_-5px_var(--glow)] relative`}
                  style={{ '--glow': s.glow }}>
                  <s.icon className="w-10 h-10 text-white" strokeWidth={1.5} />
                  {/* Pulse ring */}
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-30 group-hover:animate-ping" style={{ backgroundColor: s.glow }} />
                </div>

                {/* Step badge */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-bold tracking-widest uppercase px-3 py-1.5 rounded-full border"
                    style={{ color: s.glow, borderColor: `${s.glow}40`, backgroundColor: `${s.glow}15` }}>
                    Крок {s.num}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r"
                  style={{ '--tw-gradient-from': s.glow, '--tw-gradient-to': '#fff' }}>
                  {s.title}
                </h3>
                <p className="text-slate-400 leading-relaxed text-sm group-hover:text-slate-300 transition-colors">{s.desc}</p>
              </div>

              {/* Arrow connector */}
              {i < steps.length - 1 && i % 3 !== 2 && (
                <div className="hidden lg:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10 items-center">
                  <div className="w-6 h-[2px] bg-gradient-to-r from-slate-600 to-slate-500" />
                  <ArrowRight className="w-4 h-4 text-slate-500" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        #how-it-works .group {
          animation: fadeInUp 0.6s ease-out both;
        }
      `}</style>
    </section>
  );
};
